import { FormEvent, useEffect, useId, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { InterestType, submitInterest, submitPublicInterviewSlotRequest } from "../../Lib/interestApi";
import type { InterviewTier } from "../../Lib/interviewApi";
import { trackFunnelEvent } from "../../Lib/funnelAnalytics";

export type InterestCategory = { value: string; label: string };

type Props = {
    interestType: InterestType;
    sourcePage: string;
    categories: InterestCategory[];
    selectedCategory?: string;
    successMessage?: string;
    showAcademicFields?: boolean;
    categoryLabel?: string;
    preparationLabel?: string;
    submissionMode?: "interest" | "interview_slot_request";
    lockCategory?: boolean;
    showCollege?: boolean;
    showTargetRole?: boolean;
    showPreferredDate?: boolean;
    successTitle?: string;
    submitLabel?: string;
};

export default function InterestForm({ interestType, sourcePage, categories, selectedCategory = "", successMessage = "Thanks! We'll let you know when relevant opportunities open.", showAcademicFields = false, categoryLabel = "What are you preparing for?", preparationLabel, submissionMode = "interest", lockCategory = false, showCollege = true, showTargetRole = false, showPreferredDate = false, successTitle = "Interest submitted", submitLabel = "Submit Interest" }: Props) {
    const id = useId();
    const [fields, setFields] = useState({ name: "", email: "", mobile: "", college: "", branch: "", year: "", selectedCategory, preparation: "", targetRole: "", preferredDate: "", website: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => setFields((current) => ({ ...current, selectedCategory })), [selectedCategory]);

    const update = (field: keyof typeof fields, value: string) => setFields((current) => ({ ...current, [field]: value }));
    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setSubmitting(true); setError("");
        try {
            if (submissionMode === "interview_slot_request") {
                await submitPublicInterviewSlotRequest({
                    name: fields.name, email: fields.email, mobile: fields.mobile,
                    tier: fields.selectedCategory as InterviewTier, targetRole: fields.targetRole,
                    ...(fields.preferredDate ? { preferredDate: fields.preferredDate } : {}),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
                    sourcePage, website: fields.website,
                });
                trackFunnelEvent("slot_request", { productType: "MOCK_INTERVIEW", tier: fields.selectedCategory, sourcePage });
            } else {
                await submitInterest({ ...fields, interestType, sourcePage });
                trackFunnelEvent("interest_submit", { interestType, sourcePage, selectedCategory: fields.selectedCategory });
            }
            setSubmitted(true);
        } catch (nextError: any) {
            setError(nextError?.message || "Your interest could not be submitted. Please try again.");
        } finally { setSubmitting(false); }
    };

    if (submitted) return <div className="mz-interest-success" role="status"><CheckCircle2 /><h3>{successTitle}</h3><p>{successMessage}</p></div>;

    return <form className="mz-interest-form" onSubmit={submit}>
        <input aria-hidden="true" tabIndex={-1} autoComplete="off" name="website" value={fields.website} onChange={(event) => update("website", event.target.value)} className="sr-only" />
        <div className="mz-interest-form__row">
            <Field id={`${id}-name`} label="Name"><input id={`${id}-name`} required autoComplete="name" value={fields.name} onChange={(event) => update("name", event.target.value)} /></Field>
            <Field id={`${id}-email`} label="Email"><input id={`${id}-email`} required type="email" autoComplete="email" value={fields.email} onChange={(event) => update("email", event.target.value)} /></Field>
        </div>
        <div className={`mz-interest-form__row ${showCollege ? "" : "mz-interest-form__row--single"}`}>
            <Field id={`${id}-mobile`} label="Mobile"><input id={`${id}-mobile`} required inputMode="tel" autoComplete="tel" value={fields.mobile} onChange={(event) => update("mobile", event.target.value)} /></Field>
            {showCollege && <Field id={`${id}-college`} label="College"><input id={`${id}-college`} required autoComplete="organization" value={fields.college} onChange={(event) => update("college", event.target.value)} /></Field>}
        </div>
        {showAcademicFields && <div className="mz-interest-form__row">
            <Field id={`${id}-branch`} label="Branch"><input id={`${id}-branch`} required value={fields.branch} onChange={(event) => update("branch", event.target.value)} /></Field>
            <Field id={`${id}-year`} label="Year"><input id={`${id}-year`} required placeholder="For example, 3rd year" value={fields.year} onChange={(event) => update("year", event.target.value)} /></Field>
        </div>}
        <Field id={`${id}-category`} label={categoryLabel}>
            {lockCategory ? <><input type="hidden" value={fields.selectedCategory} /><div id={`${id}-category`} className="mz-interest-form__locked-value">{categories.find((category) => category.value === fields.selectedCategory)?.label || fields.selectedCategory}</div></> : <select id={`${id}-category`} required value={fields.selectedCategory} onChange={(event) => update("selectedCategory", event.target.value)}>
                <option value="">Choose one</option>
                {categories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
            </select>}
        </Field>
        {showTargetRole && <Field id={`${id}-target-role`} label="Target role"><input id={`${id}-target-role`} required placeholder="For example, Frontend Developer" value={fields.targetRole} onChange={(event) => update("targetRole", event.target.value)} /></Field>}
        {showPreferredDate && <Field id={`${id}-preferred-date`} label="Preferred date (optional)"><input id={`${id}-preferred-date`} type="date" min={new Date().toISOString().slice(0, 10)} value={fields.preferredDate} onChange={(event) => update("preferredDate", event.target.value)} /></Field>}
        {preparationLabel && <Field id={`${id}-preparation`} label={preparationLabel}><textarea id={`${id}-preparation`} rows={3} value={fields.preparation} onChange={(event) => update("preparation", event.target.value)} /></Field>}
        {error && <p className="mz-interest-form__error" role="alert">{error}</p>}
        <button type="submit" className="mz-button mz-button--primary mz-button--large" disabled={submitting}>{submitting ? <><Loader2 className="animate-spin" />Submitting…</> : submitLabel}</button>
    </form>;
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
    return <label htmlFor={id}><span>{label}</span>{children}</label>;
}
